// What each cargo row actually prints, and why a stored line does not print the
// amount it stores.
//
// @aiInvariant Every amount the sheet renders for one cargo — the six typed cost
//   cells and the stored legacy rows together — adds up per currency to that
//   cargo's frozen totals, and the batch-wide services add up to whatever the
//   frozen document totals still owe once those rows have printed. Nothing here
//   changes a stored value or a frozen total: the reconciliation is
//   display-only.
import {
  formatSheetAmount,
  type LogisticsCalculationCurrency,
  type LogisticsCalculationMoneyTotals,
  type LogisticsCalculationPublicDto,
} from '@magimp/shared';

export type Cargo = LogisticsCalculationPublicDto['cargos'][number];
export type PublicLine = LogisticsCalculationPublicDto['commonServices'][number];
export type Cost = NonNullable<Cargo['transport']>;

export const SHEET_COST_FIELDS = [
  'transport',
  'insurance',
  'packaging',
  'extras',
  'prr',
  'clientDelivery',
] as const;
export type SheetCostField = (typeof SHEET_COST_FIELDS)[number];
export type SingleCostField = Exclude<SheetCostField, 'extras'>;

const CURRENCIES = ['USD', 'CNY', 'RUB'] as const;
const TOTALS_KEY = { USD: 'usd', CNY: 'cny', RUB: 'rub' } as const;

type Cents = Record<LogisticsCalculationCurrency, bigint>;

/** A stored service line as the sheet prints it inside the extras cell. */
export interface RenderedLine {
  index: number;
  serviceName: string;
  amount: string;
  currency: LogisticsCalculationCurrency | null;
  /** The stored yuan face a converted line was booked away from. */
  faceCaption: string | null;
}

export interface CargoRowAmounts {
  costs: Record<SingleCostField, Cost | null>;
  extras: Cost[];
  legacy: RenderedLine[];
}

export function toCents(amount: string | null | undefined): bigint {
  if (!amount) return 0n;
  const negative = amount.startsWith('-');
  const [whole = '0', fraction = ''] = (negative ? amount.slice(1) : amount).split('.');
  const scaled = BigInt(whole || '0') * 100n + BigInt(fraction.padEnd(2, '0').slice(0, 2));
  return negative ? -scaled : scaled;
}

export function fromCents(cents: bigint): string {
  const negative = cents < 0n;
  const digits = (negative ? -cents : cents).toString().padStart(3, '0');
  return `${negative ? '-' : ''}${digits.slice(0, -2)}.${digits.slice(-2)}`;
}

/**
 * The amount a stored line is actually booked at.
 *
 * A `convertToUsd` line leaves the yuan total entirely, so printing its stored
 * face would show the customer rows that do not add up to the total above them.
 */
function bookedAmount(line: PublicLine): {
  amount: string;
  currency: LogisticsCalculationCurrency | null;
} {
  if (line.convertedAmountUsd !== null && line.convertedAmountUsd !== undefined) {
    return { amount: line.convertedAmountUsd, currency: 'USD' };
  }
  return { amount: line.amount ?? '0.00', currency: line.currency ?? null };
}

/**
 * Spreads a rounding residual across the rows of one currency group.
 *
 * `addLine` accumulates the exact rational into the totals but rounds each
 * projected line on its own, so the rounded rows can miss the frozen total by
 * cents. The residual is placed largest booked amount first, ties to the first
 * stored row, and never past a row's own sign — concentrating it on one row
 * would print a negative amount for a service that cost a positive one.
 */
function allocate(
  rows: ReadonlyArray<{ index: number; cents: bigint }>,
  residual: bigint,
): Map<number, bigint> {
  const adjustment = new Map(rows.map((row) => [row.index, 0n]));
  let pool = [...rows].sort((a, b) =>
    a.cents === b.cents ? a.index - b.index : a.cents > b.cents ? -1 : 1,
  );
  let remaining = residual;
  while (remaining !== 0n && pool.length > 0) {
    const share = remaining / BigInt(pool.length);
    const step = remaining < 0n ? -1n : 1n;
    const spread = (remaining - share * BigInt(pool.length)) * step;
    let placed = 0n;
    const next: typeof pool = [];
    pool.forEach((row, position) => {
      const want = share + (BigInt(position) < spread ? step : 0n);
      const current = row.cents + adjustment.get(row.index)!;
      const granted = current + want < 0n ? -current : want;
      adjustment.set(row.index, adjustment.get(row.index)! + granted);
      placed += granted;
      if (current + granted > 0n) next.push(row);
    });
    remaining -= placed;
    if (placed === 0n) break;
    pool = next;
  }
  return adjustment;
}

/**
 * The stored lines of one group with the residual allocated against `target`.
 *
 * `target` is the group's own share of the frozen totals — for a cargo, what is
 * left of its totals once the typed cost cells are taken out, so a mixed-shape
 * cargo never shifts a stored line onto a typed cost.
 */
export function renderedLegacyLines(
  lines: readonly PublicLine[],
  target: LogisticsCalculationMoneyTotals | null,
): RenderedLine[] {
  const booked = lines.map((line, index) => ({ index, ...bookedAmount(line) }));
  const adjustments = new Map<number, bigint>();
  if (target) {
    for (const currency of CURRENCIES) {
      const rows = booked
        .filter((entry) => entry.currency === currency)
        .map((entry) => ({ index: entry.index, cents: toCents(entry.amount) }));
      if (rows.length === 0) continue;
      const placed = rows.reduce((sum, row) => sum + row.cents, 0n);
      const residual = toCents(target[TOTALS_KEY[currency]]) - placed;
      for (const [index, adjustment] of allocate(rows, residual)) {
        adjustments.set(index, adjustment);
      }
    }
  }
  return lines.map((line, index) => {
    const entry = booked[index]!;
    return {
      index,
      serviceName: line.serviceName,
      amount: fromCents(toCents(entry.amount) + (adjustments.get(index) ?? 0n)),
      currency: entry.currency,
      faceCaption:
        line.convertedAmountUsd === null || line.convertedAmountUsd === undefined
          ? null
          : formatSheetAmount(line.amount ?? '0.00', 'CNY'),
    };
  });
}

function emptyCents(): Cents {
  return { USD: 0n, CNY: 0n, RUB: 0n };
}

/** The typed cost cells of a cargo, in the order the sheet prints them. */
export function cargoCostCells(cargo: Cargo | null): Cost[] {
  if (!cargo) return [];
  return [
    cargo.transport,
    cargo.insurance,
    cargo.packaging,
    ...(cargo.extras ?? []),
    cargo.prr,
    cargo.clientDelivery,
  ].filter((cost): cost is Cost => cost !== undefined);
}

/** What is left of a cargo's frozen totals once its typed cost cells are out. */
function legacyTarget(cargo: Cargo): LogisticsCalculationMoneyTotals {
  const visible = emptyCents();
  for (const cost of cargoCostCells(cargo)) visible[cost.currency] += toCents(cost.amount);
  return {
    usd: fromCents(toCents(cargo.totals.usd) - visible.USD),
    cny: fromCents(toCents(cargo.totals.cny) - visible.CNY),
    rub: fromCents(toCents(cargo.totals.rub) - visible.RUB),
  };
}

export function cargoRowAmounts(cargo: Cargo | null): CargoRowAmounts {
  return {
    costs: {
      transport: cargo?.transport ?? null,
      insurance: cargo?.insurance ?? null,
      packaging: cargo?.packaging ?? null,
      prr: cargo?.prr ?? null,
      clientDelivery: cargo?.clientDelivery ?? null,
    },
    extras: cargo?.extras ?? [],
    legacy: cargo ? renderedLegacyLines(cargo.lines, legacyTarget(cargo)) : [],
  };
}

/**
 * What the frozen document totals still owe once the cargo rows have printed.
 *
 * WHY not the frozen `commonServicesTotals`: `calculation.ts` rounds each cargo
 * total, the batch-wide total and the document total independently (`:252`,
 * `:269`, `:270`), so a sub-cent line in a cargo and a sub-cent batch-wide
 * service can each round up to a cent under a document total that rounds down —
 * two printed cents beneath a grand-total cell reading one. Booking the
 * batch-wide rows against the remainder keeps the sheet the customer adds up
 * equal to the total it is signed off against, and leaves every cargo row on its
 * own frozen totals.
 */
export function commonServicesTarget(
  rows: readonly CargoRowAmounts[],
  documentTotals: LogisticsCalculationMoneyTotals | null,
): LogisticsCalculationMoneyTotals | null {
  if (!documentTotals) return null;
  const printed = emptyCents();
  for (const field of SHEET_COST_FIELDS) {
    const column = columnTotals(rows, field);
    printed.USD += toCents(column.usd);
    printed.CNY += toCents(column.cny);
    printed.RUB += toCents(column.rub);
  }
  return {
    usd: fromCents(toCents(documentTotals.usd) - printed.USD),
    cny: fromCents(toCents(documentTotals.cny) - printed.CNY),
    rub: fromCents(toCents(documentTotals.rub) - printed.RUB),
  };
}

/** Per-currency sum of one printed column, across every cargo row. */
export function columnTotals(
  rows: readonly CargoRowAmounts[],
  field: SheetCostField,
): LogisticsCalculationMoneyTotals {
  const totals = emptyCents();
  for (const row of rows) {
    if (field === 'extras') {
      for (const extra of row.extras) totals[extra.currency] += toCents(extra.amount);
      for (const line of row.legacy) {
        if (line.currency) totals[line.currency] += toCents(line.amount);
      }
      continue;
    }
    const cost = row.costs[field];
    if (cost) totals[cost.currency] += toCents(cost.amount);
  }
  return { usd: fromCents(totals.USD), cny: fromCents(totals.CNY), rub: fromCents(totals.RUB) };
}
